import React from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash/debounce';
import gettext from 'js-utils/gettext';

import Label from 'eventbrite_design_system/label/Label';
import Icon from 'eventbrite_design_system/icon/Icon';
import MapPinFillSvg from 'eventbrite_design_system/iconography/icons/MapPinFill';
import {
    addWindowListener,
    removeWindowListener,
} from 'eventbrite_design_system/utils/dom';

import ConnectedLocationAutocompleteInternal from '../containers/ConnectedLocationAutocompleteInternal';

import './locationSwitcher.scss';

const LOCATION_DEBOUNCE_INTERVAL = 200;
const LOCATION_PICKER_ICON_TYPE = <MapPinFillSvg />;
const PAGE_SHOW_EVENT_NAME = 'pageshow';

export default class LocationSwitcher extends React.PureComponent {
    static propTypes = {
        handleLocationSuggestionChange: PropTypes.func.isRequired,
        currentPlace: PropTypes.string,
        handleUseCurrentLocation: PropTypes.func,
        handleLocationSelect: PropTypes.func,
    };

    _handleComponentDidMount = () => {
        addWindowListener(PAGE_SHOW_EVENT_NAME, this._handlePageLoadFromCache);
    };

    _handleComponentWillUnmount = () => {
        removeWindowListener(
            PAGE_SHOW_EVENT_NAME,
            this._handlePageLoadFromCache,
        );
    };

    /**
     * Helper function which consumes the `pageshow` event to determine if the
     * page was served from the browser cache. Allows for a hook such that any
     * state resetting or other behaviors can occur.
     */
    _handlePageLoadFromCache = (event) => {
        const { currentPlace } = this.props;

        if (event.persisted) {
            //For browsers which utilize back-forward cache, the store is cached with the
            //autocomplete suggestions previously entered by the user so we want to display
            //suggestions which make sense in the context of the current state.
            this._requestLocationSuggestions(currentPlace);
        }
    };

    _requestLocationSuggestions = debounce((locationQuery) => {
        const { handleLocationSuggestionChange } = this.props;

        handleLocationSuggestionChange(locationQuery);
    }, LOCATION_DEBOUNCE_INTERVAL);

    render() {
        const {
            handleUseCurrentLocation,
            handleLocationSelect,
            isOnline,
            history,
        } = this.props;
        const placeholder = gettext('search for a place');
        const browseText = isOnline
            ? gettext('Browsing')
            : gettext('Browsing in');

        return (
            <nav className="city-browse-header__nav eds-l-pad-top-3 eds-align--center-vertical">
                <span className="city-browse-header__nav-item eds-l-pad-left-3 eds-l-pad-bot-2">
                    <Label htmlFor="locationPicker">
                        <Icon type={LOCATION_PICKER_ICON_TYPE} />
                    </Label>
                </span>
                <div className="city-browse-header__nav-text">
                    <span className="city-browse-header__nav-item location-picker__label">
                        <Label htmlFor="locationPicker">
                            <span
                                className="eds-l-pad-left-1 eds-text-color--grey-700 eds-text-weight--light "
                                data-spec="location-picker-browse-text"
                            >
                                {browseText}
                            </span>
                        </Label>
                    </span>
                    <span className="city-browse-header__nav-item location-picker__field">
                        <ConnectedLocationAutocompleteInternal
                            autocompleteProps={{
                                id: 'locationSwitcherPicker',
                                name: 'locationSwitcherPicker',
                                borderType: 'hidden',
                                hideOutline: true,
                                isLimitedSearch: true,
                                preventInputValuePrefill: true,
                            }}
                            handleUseCurrentLocation={handleUseCurrentLocation}
                            placeholder={placeholder}
                            onComponentDidMount={this._handleComponentDidMount}
                            onComponentWillUnmount={
                                this._handleComponentWillUnmount
                            }
                            handleLocationSelect={handleLocationSelect}
                            history={history}
                        />
                    </span>
                </div>
            </nav>
        );
    }
}
